import { Card } from '@/components/ui/card'
import { formatCurrency } from '@/lib/format'
import { computePreviewByVatRate, type VatSubtotal } from '@/lib/creditNoteHelpers'

interface CreditPreviewBlockProps {
  sourceSubtotals: VatSubtotal[]
  sourceTtc: number
  partialTtc: number
  errorMessage?: string | null
  // When the listing DTO doesn't ship a reliable per-rate breakdown
  // (multi-VAT source), the FE can't safely guess the split — show the total
  // only and a note that the BE computes the real breakdown at emission.
  // SOLY-CN-001 mini-scope 04 — eng review P2-3.
  hideRateBreakdown?: boolean
}

/**
 * Live preview of Mode B partial proportional credit. Shows the per-VAT-rate
 * breakdown that mirrors the BE handler computation, so Élodie sees how her
 * TTC input splits across 6% / 21% subtotals before submitting.
 * SOLY-CN-001 mini-scope 04.
 */
export function CreditPreviewBlock({
  sourceSubtotals,
  sourceTtc,
  partialTtc,
  errorMessage,
  hideRateBreakdown,
}: CreditPreviewBlockProps) {
  if (errorMessage) {
    return (
      <Card
        data-testid="credit-note-preview-block"
        className="p-3 border-destructive/40 bg-destructive/5 text-sm text-destructive"
      >
        {errorMessage}
      </Card>
    )
  }

  if (hideRateBreakdown) {
    if (partialTtc <= 0 || partialTtc >= sourceTtc) return null
    return (
      <Card data-testid="credit-note-preview-block" className="p-3 space-y-1 text-sm">
        <div className="font-medium">
          Sera crédité : {formatCurrency(partialTtc)} TTC
        </div>
        <div className="text-xs text-muted-foreground">
          Décomposition par taux de TVA calculée à l'émission (facture multi-taux).
        </div>
      </Card>
    )
  }

  const preview = computePreviewByVatRate(sourceSubtotals, partialTtc, sourceTtc)
  if (preview.length === 0) return null

  const totalTtc = preview.reduce((sum, line) => sum + line.ttc, 0)

  return (
    <Card data-testid="credit-note-preview-block" className="p-3 space-y-2 text-sm">
      <div className="font-medium">Sera crédité :</div>
      <ul className="space-y-1">
        {preview.map((line) => (
          <li key={line.vatPercent} className="text-muted-foreground">
            ·{' '}
            <span className="font-medium text-foreground">{formatCurrency(line.ttc)}</span>{' '}
            TTC à {line.vatPercent} % (HT {formatCurrency(line.ht)} + TVA {formatCurrency(line.vat)})
          </li>
        ))}
      </ul>
      <div className="pt-1 border-t font-medium">
        Total : {formatCurrency(totalTtc)} TTC
      </div>
    </Card>
  )
}
